import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
} from '@angular/core';
import { StateIndicatorComponent } from './state-indicator.component';
import { StateIndicator } from './state-indicator.model';

/**
 * The `MultiStateIndicatorComponent` represents a collection of state indicators
 * with customizable styles and optional custom content for each state.
 */
@Component({
  selector: 'ui-multi-state-indicator',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [StateIndicatorComponent],
  template: `
    <div class="flex flex-wrap gap-1">
      @for (item of indicatorsSignal(); track item.index) {
        <ui-state-indicator [state]="item.status" [states]="statesSignal()" [indicator]="item" />
      }
    </div>
  `,
})
export class MultiStateIndicatorComponent implements AfterViewInit {
  DEFAULT_STATES = {
    default: { class: 'border border-gray-300 bg-white' },
    active: { class: 'bg-primary' },
    disabled: { class: 'bg-primary' },
  };

  /**
   * The list of indicators to display.
   */
  indicators = input<any[]>([]);

  /**
   * A signal representing the indicators configuration.
   * This enables reactivity and ensures that updates are tracked automatically.
   */
  indicatorsSignal = computed(() => this.indicators().map((indicator, index) => ({...indicator, index})));

  /**
   * The definition of states with their corresponding styles and optional content.
   */
  states = input<StateIndicator | null>(null);

  /**
   * A signal representing the current state configuration.
   * This enables reactivity and ensures that updates are tracked automatically.
   */
  statesSignal = signal(this.states() || null);

  /**
   * Lifecycle hook invoked after the view is fully initialized.
   * Updates the state configuration to include a template for the "disabled" state.
   */
  ngAfterViewInit(): void {
    const statesLoaded = !this.states() ? this.DEFAULT_STATES : this.states();
    this.statesSignal.set(statesLoaded);
  }
}
